import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  PerformanceGoalsUpdateMetricGroupDocument,
  PerformanceGoalsUpdateMetricGroupFragment,
  PerformanceGoalsUpdateMetricGroupMutationVariables,
} from 'generated/resources/graphql'
import { DocumentNode } from 'graphql'

import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { PublicSelectField } from 'modules/goals/components/PublicSelectField'
import { validateTitle } from 'modules/goals/lib/keyResultsValidations'
import { CycleSelectField } from 'modules/performance_cycles/components/CycleSelectField'

export const UpdateMetricGroupModal: React.FC<{
  metricGroup: PerformanceGoalsUpdateMetricGroupFragment
  refetchQueries?: DocumentNode[]
  onClose: () => void
}> = ({ metricGroup: metric, onClose, refetchQueries }) => {
  const [update] = useMutation(PerformanceGoalsUpdateMetricGroupDocument, {
    extractorFn: (data) => data.goalsMutations.updateGoalsMetricGroup,
    refetchQueries,
    awaitRefetchQueries: true,
  })

  const form = useForm<PerformanceGoalsUpdateMetricGroupMutationVariables>({
    defaultValues: {
      id: metric.id,
      cycleIds: metric.performanceCycles.map((cycle) => cycle.id),
      title: metric.title,
      description: metric.description,
      public: metric.public,
    },

    validate(values) {
      const errors: Partial<typeof values> = {}

      const titleError = validateTitle(values.title)
      if (titleError) {
        errors.title = titleError
      }

      return errors
    },

    onSubmit: async (variables) => {
      const result = await update({ variables })
      const handled = handleResult(result, () => null)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onClose()
      }

      return handled
    },
  })

  return (
    <Modal
      title={i18n.t('performance_goals.update_goal_modal.title')}
      onClose={onClose}
      illustration="analytics-graph-lines"
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button onClick={() => onClose()} type="secondary" disabled={form.isSubmitting}>
            {i18n.t('performance_goals.update_goal_modal.actions.cancel')}
          </Button>
          <Button submit disabled={form.isSubmitting}>
            {i18n.t('performance_goals.update_goal_modal.actions.update')}
          </Button>
        </>
      }
    >
      <Box gap="s12">
        <TextField
          error={form.errors.title?.toString()}
          label={i18n.t('performance_goals.metric_group_fields.title.label')}
          value={form.values.title}
          onChange={(value) => form.setFieldValue('title', value)}
        />
        <TextArea
          error={form.errors.title?.toString()}
          label={i18n.t('performance_goals.metric_group_fields.description.label')}
          value={form.values.description?.toString()}
          onChange={(value) => form.setFieldValue('description', value)}
        />
        <CycleSelectField
          label={i18n.t('performance_goals.cycles.cycle_select_field.label')}
          blankLabel={i18n.t('performance_goals.cycles.cycle_select_field.blank_label')}
          placeholder={i18n.t('performance_goals.cycles.cycle_select_field.placeholder')}
          value={form.values.cycleIds ? form.values.cycleIds[0] : undefined}
          onChange={(value) => form.setFieldValue('cycleIds', value ? [value] : [])}
        />
        <PublicSelectField
          value={form.values.public}
          onChange={(value) => form.setFieldValue('public', value)}
        />
      </Box>
    </Modal>
  )
}
